import { PERMISSIONS } from '@ibe/constants';
import {
  Button,
  ConfirmDialog,
  DataTable,
  Field,
  FieldLabel,
  FieldSelect,
  Pagination,
  SearchInput,
  StatusBadge,
  toneForStatus,
  type DataTableColumn,
} from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PropertySelect, usePropertyOptions } from '../../../components/form/property-select.js';
import { ColumnPicker } from '../../../components/resource-list/column-picker.js';
import { useAppStore } from '../../../lib/state/store.js';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import {
  resolveVisibleColumns,
  type ColumnDescriptor,
} from '../../../lib/resource-list/columns.js';
import { useSoftDeleteNotificationTemplate } from '../api/mutations.js';
import { useNotificationTemplates } from '../api/queries.js';
import { CHANNEL, CHANNEL_OPTIONS, type NotificationTemplateRow } from '../api/schemas.js';

const ALL_CHANNELS = [{ value: '', label: 'All channels' }, ...CHANNEL_OPTIONS];

const DEFAULT_SORT = { columnId: 'updatedAt', direction: 'desc' } as const;
const PAGE_SIZE = 10;
const FIRST_PAGE = 1;

const COLUMNS: readonly ColumnDescriptor<NotificationTemplateRow>[] = [
  {
    id: 'name',
    header: 'Template',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'code'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/notification-templates/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'channel',
    header: 'Channel',
    sortable: true,
    fields: ['channel'],
    cell: (row) => row.channel,
  },
  {
    id: 'type',
    header: 'Type',
    sortable: true,
    fields: ['type'],
    cell: (row) => row.type,
  },
  {
    id: 'default',
    header: 'Default',
    fields: ['isDefault'],
    cell: (row) => (row.isDefault ? 'Yes' : 'No'),
  },
  {
    id: 'status',
    header: 'Status',
    fields: ['isActive'],
    cell: (row) =>
      row.isActive === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.isActive ? 'ACTIVE' : 'INACTIVE')}>
          {row.isActive ? 'Active' : 'Inactive'}
        </StatusBadge>
      ),
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    sortable: true,
    fields: ['updatedAt'],
    cell: (row) =>
      row.updatedAt === undefined ? null : new Date(row.updatedAt).toLocaleDateString('en-AU'),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: NotificationTemplateRow) => <NotificationTemplateRowActions row={row} />,
  },
];

const RESOURCE = 'notificationTemplates';

function NotificationTemplateRowActions({
  row,
}: {
  readonly row: NotificationTemplateRow;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteNotificationTemplate();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/notification-templates/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/notification-templates/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete notification template"
        description={`${label} will be removed. Its audit history and any dispatches that used it are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

/** Filters the fetched rows by the user's search term. */
function filterRows(
  rows: readonly NotificationTemplateRow[],
  q: string,
): readonly NotificationTemplateRow[] {
  const needle = q.trim().toLowerCase();
  if (needle === '') return rows;
  return rows.filter((row) =>
    [row.name, row.code, row.type]
      .filter((value): value is string => typeof value === 'string')
      .some((value) => value.toLowerCase().includes(needle)),
  );
}

/** Sorts the rows using the requested column and direction. */
function sortRows(
  rows: readonly NotificationTemplateRow[],
  sortBy: string,
  sortDirection: 'asc' | 'desc',
): readonly NotificationTemplateRow[] {
  const sorted = [...rows];
  const direction = sortDirection === 'asc' ? 1 : -1;

  sorted.sort((left, right) => {
    const leftValue = left[sortBy as keyof NotificationTemplateRow];
    const rightValue = right[sortBy as keyof NotificationTemplateRow];

    if (leftValue === rightValue) return 0;
    if (leftValue === undefined || leftValue === null) return direction;
    if (rightValue === undefined || rightValue === null) return -direction;

    if (typeof leftValue === 'string' && typeof rightValue === 'string') {
      return leftValue.localeCompare(rightValue) * direction;
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * direction;
    }

    if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
      return (Number(leftValue) - Number(rightValue)) * direction;
    }

    return String(leftValue).localeCompare(String(rightValue)) * direction;
  });

  return sorted;
}

export function NotificationTemplateListPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const channel = params.get('channel') ?? '';
  const sortBy = params.get('sortBy') ?? DEFAULT_SORT.columnId;
  const sortDirection = (params.get('sortDirection') ?? DEFAULT_SORT.direction) as 'asc' | 'desc';

  const selectedChannel = channel === '' ? CHANNEL.EMAIL : channel;
  const storedPropertyId = useAppStore(
    (state) => state.ui.lastPropertyIds?.['notification-templates'] ?? '',
  );
  const setLastPropertyId = useAppStore((state) => state.setLastPropertyId);
  const columnSelection = useAppStore((state) => state.prefs.columns[RESOURCE]);
  const properties = usePropertyOptions();

  const page = Math.max(
    FIRST_PAGE,
    Number.isNaN(Number(params.get('page'))) ? FIRST_PAGE : Number(params.get('page')),
  );

  useEffect(() => {
    if (properties.isPending || properties.data === undefined) return;
    const valid = properties.data.some((option) => option.value === storedPropertyId);
    if (storedPropertyId !== '' && valid) return;

    const first = properties.data[0]?.value ?? '';
    if (first !== storedPropertyId) {
      setLastPropertyId('notification-templates', first);
    }
  }, [properties.data, properties.isPending, setLastPropertyId, storedPropertyId]);

  const query = useNotificationTemplates(
    selectedChannel,
    storedPropertyId === '' ? undefined : storedPropertyId,
  );

  const rows = useMemo(() => {
    const filtered = filterRows(query.data ?? [], q);
    return sortRows(filtered, sortBy, sortDirection);
  }, [query.data, q, sortBy, sortDirection]);

  const visibleColumns: readonly DataTableColumn<NotificationTemplateRow>[] = useMemo(
    () => resolveVisibleColumns(COLUMNS, columnSelection),
    [columnSelection],
  );

  const totalItems = rows.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;
  const safePage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, safePage]);

  const meta = useMemo(
    () => ({
      page: safePage,
      pageSize: PAGE_SIZE,
      totalItems,
      totalPages,
      hasNextPage: safePage < totalPages,
      hasPreviousPage: safePage > FIRST_PAGE,
    }),
    [safePage, totalItems, totalPages],
  );

  const update = (changes: Readonly<Record<string, string | undefined>>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  return (
    <PermissionGuard permission={PERMISSIONS.NOTIFICATION_READ} fallback={<NotPermitted />}>
      <section className="space-y-section">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-page-title font-semibold">Notification templates</h1>
            <p className="text-body text-muted-foreground">
              Email and SMS messages the platform sends for each notification type.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ColumnPicker resource={RESOURCE} columns={COLUMNS} />
            <PermissionGuard permission={PERMISSIONS.NOTIFICATION_CONFIGURE}>
              <Button asChild>
                <Link to="/notification-templates/new">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  New template
                </Link>
              </Button>
            </PermissionGuard>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <SearchInput
            label="Search templates"
            className="h-10 md:mt-5"
            defaultValue={q}
            onSearch={(term) => {
              update({ q: term, page: undefined });
            }}
          />
          <Field>
            <FieldLabel>Channel</FieldLabel>
            <FieldSelect
              value={channel}
              onValueChange={(value) => {
                update({ channel: value, page: undefined });
              }}
              options={ALL_CHANNELS}
              placeholder="All channels"
            />
          </Field>
          <PropertySelect
            value={storedPropertyId}
            onChange={(value) => {
              setLastPropertyId('notification-templates', value);
              update({ page: undefined });
            }}
            description="Filter by property"
          />
        </div>

        <DataTable
          caption="Notification templates"
          columns={visibleColumns}
          rows={pagedRows}
          rowKey={(row) => row.id}
          loading={query.isPending}
          error={errorTextFor(query.error) ?? undefined}
          onRetry={() => {
            void query.refetch();
          }}
          sort={{ columnId: sortBy, direction: sortDirection }}
          onSortChange={(next) => {
            update({ sortBy: next.columnId, sortDirection: next.direction });
          }}
          emptyTitle="No templates found"
          emptyDescription="Add a template for the selected channel."
        />

        {!query.isPending && totalItems > 0 && (
          <Pagination
            meta={meta}
            itemNoun={{ one: 'template', many: 'templates' }}
            onPageChange={(next) => {
              update({ page: String(next) });
            }}
          />
        )}
      </section>
    </PermissionGuard>
  );
}
